import { useEffect } from 'react'
import type { Language } from '../../models/LandingPage'
import { setPageMetadata } from '../../utils/seo'

export type LegalRoute = 'privacy' | 'cookies' | 'terms'

type LegalSection = {
  title: string
  body: string[]
}

type LegalPageContent = {
  eyebrow: string
  title: string
  metaTitle: string
  description: string
  canonicalPath: string
  updated: string
  intro: string
  sections: LegalSection[]
}

const privacyEmail = 'privacy@tonesignature.com'
const generalEmail = 'hello@tonesignature.com'

const legalPages: Record<Language, Record<LegalRoute, LegalPageContent>> = {
  en: {
    privacy: {
      eyebrow: 'Legal',
      title: 'Privacy Policy',
      metaTitle: 'Privacy Policy | ToneSignature',
      description: 'Privacy Policy for ToneSignature, including beta registration data, analytics consent, GDPR rights, retention, and privacy contact details.',
      canonicalPath: '/privacy',
      updated: 'Last updated: July 17, 2026',
      intro: 'This Privacy Policy explains how ToneSignature collects and uses personal data for the public landing page and Founding Beta registration.',
      sections: [
        {
          title: 'Data Controller',
          body: [
            'ToneSignature is currently an independent software project operated by Omer Sorrenti.',
            'Until a legal entity is established, Omer Sorrenti acts as the data controller for the personal data collected through this website.',
            `For privacy questions or requests, contact ${privacyEmail}.`,
          ],
        },
        {
          title: 'Information We Collect',
          body: [
            'When you join the Founding Beta, we collect the name, email address, country, selected guitar hardware, primary use, preferred language, and beta acknowledgement that you submit through the registration form.',
            'If you consent to analytics cookies, we may collect limited analytics events such as page interactions, language changes, hero call-to-action clicks, beta form events, supported hardware interactions, and other product-interest signals.',
          ],
        },
        {
          title: 'Why We Use Personal Data',
          body: [
            'We use beta registration data to manage the Founding Beta list, communicate with prospective beta users, prioritize hardware support, prevent duplicate registrations, and plan product development.',
            'If analytics consent is granted, analytics events help us understand whether the landing page and demo are clear, reliable, and useful.',
          ],
        },
        {
          title: 'Legal Basis',
          body: [
            'For beta registration management and related communication, we rely on your request to take steps before entering a possible future agreement and on our legitimate interest in operating an early-stage beta.',
            'For analytics cookies and analytics events, we rely on your consent. You can reject analytics cookies without affecting your ability to use the website or join the beta.',
          ],
        },
        {
          title: 'Third-Party Services',
          body: [
            'ToneSignature may use third-party service providers for hosting, database storage, email delivery, and analytics. These providers process personal data only as necessary to operate the website, manage beta registrations, and deliver related communications.',
            'Analytics providers are used only after you give consent and only when an analytics provider ID is configured. This policy remains vendor-neutral until production providers are finalized.',
          ],
        },
        {
          title: 'Data Retention',
          body: [
            'Beta registration data is retained until the beta program ends or until you request deletion, unless a longer retention period is required by law.',
            'Analytics data, when collected with consent, is retained according to the configured analytics provider settings and reviewed periodically for minimization.',
          ],
        },
        {
          title: 'Your GDPR Rights',
          body: [
            'If GDPR applies to you, you may request access, correction, deletion, restriction, portability, or objection to processing of your personal data. You may also withdraw analytics consent at any time.',
            `You also have the right to lodge a complaint with your local data protection authority. To exercise rights, email ${privacyEmail}.`,
          ],
        },
        {
          title: 'Changes to This Policy',
          body: [
            'We may update this Privacy Policy as ToneSignature moves from beta toward a production service. Material changes will be reflected on this page.',
          ],
        },
      ],
    },
    cookies: {
      eyebrow: 'Legal',
      title: 'Cookie Policy',
      metaTitle: 'Cookie Policy | ToneSignature',
      description: 'Cookie Policy for ToneSignature, explaining technical cookies, analytics cookies, consent, and how to revoke analytics consent.',
      canonicalPath: '/cookies',
      updated: 'Last updated: July 17, 2026',
      intro: 'This Cookie Policy explains how ToneSignature uses cookies and similar local storage technologies on the landing page.',
      sections: [
        {
          title: 'Technical Cookies and Local Storage',
          body: [
            'ToneSignature uses essential browser storage to remember your cookie consent choice. This is necessary to respect your preference and avoid showing the banner repeatedly.',
            'Technical storage does not require analytics consent and is used only for basic website operation.',
          ],
        },
        {
          title: 'Analytics Cookies',
          body: [
            'Analytics cookies and analytics scripts are optional. They are used only to understand aggregate website usage and improve the landing page, demo, and beta registration experience.',
            'Analytics is not initialized unless you choose “Accept analytics” in the cookie banner and an analytics provider ID is configured.',
          ],
        },
        {
          title: 'Rejecting or Revoking Consent',
          body: [
            'You can choose “Reject non-essential cookies” in the banner to prevent analytics from loading.',
            'You can change your choice at any time by selecting “Cookie Settings” in the footer. This reopens the cookie banner so you can accept analytics or reject non-essential cookies.',
          ],
        },
        {
          title: 'Future Updates',
          body: [
            'If ToneSignature adds additional cookies or providers, this policy will be updated before those cookies are used for non-essential purposes.',
          ],
        },
      ],
    },
    terms: {
      eyebrow: 'Legal',
      title: 'Terms of Service',
      metaTitle: 'Terms of Service | ToneSignature',
      description: 'Terms of Service for the ToneSignature beta, including beta disclaimers, user content responsibilities, generated preset limitations, and liability terms.',
      canonicalPath: '/terms',
      updated: 'Last updated: July 17, 2026',
      intro: 'These Terms of Service govern use of the ToneSignature website, interactive demo, and Founding Beta registration.',
      sections: [
        {
          title: 'Beta Service',
          body: [
            'ToneSignature is currently an early-stage beta product. Features, workflows, hardware support, generated outputs, and availability may change, be limited, or be discontinued as the product develops.',
            'The beta is provided for evaluation and feedback. Joining the beta does not guarantee access, release timing, or future availability of any specific feature.',
          ],
        },
        {
          title: 'Service Availability',
          body: [
            'ToneSignature does not provide an uptime guarantee during the beta. The website, demo, backend services, and beta registration workflow may be unavailable from time to time for maintenance, testing, or development.',
          ],
        },
        {
          title: 'User Content and Rights',
          body: [
            'You are responsible for any links, audio, files, or other content you provide to ToneSignature. You must own the rights to that content or have all permissions required to use it with the service.',
            'ToneSignature does not claim ownership of any content uploaded or submitted by users. Users retain all rights to their own content.',
            'Do not upload, submit, or process content that infringes third-party rights or violates applicable law.',
          ],
        },
        {
          title: 'Generated Presets',
          body: [
            'Generated presets, tone suggestions, parameter settings, and demo outputs are provided as creative and technical suggestions. They may require manual adjustment for your instrument, hardware, firmware, speakers, playing style, and listening environment.',
            'ToneSignature does not guarantee that a generated preset will exactly reproduce any recording, artist tone, or commercial sound.',
          ],
        },
        {
          title: 'Future Subscriptions',
          body: [
            'ToneSignature may introduce paid subscriptions, paid features, or commercial plans in the future. Joining the Founding Beta does not create a purchase obligation unless you later choose to subscribe under separate pricing terms.',
          ],
        },
        {
          title: 'Limitation of Liability',
          body: [
            'To the maximum extent permitted by law, ToneSignature is provided “as is” during the beta, without warranties of any kind. ToneSignature will not be liable for indirect, incidental, consequential, special, or punitive damages arising from use of the website, demo, beta, or generated outputs.',
            'Nothing in these Terms limits liability that cannot be limited under applicable law.',
          ],
        },
        {
          title: 'Changes to These Terms',
          body: [
            'We may update these Terms as the beta evolves. Updated terms will be posted on this page with a revised date.',
          ],
        },
        {
          title: 'Contact',
          body: [
            `For questions about these Terms, contact ${generalEmail}.`,
          ],
        },
      ],
    },
  },
  it: {
    privacy: {
      eyebrow: 'Legale',
      title: 'Informativa sulla privacy',
      metaTitle: 'Informativa sulla privacy | ToneSignature',
      description: 'Informativa sulla privacy di ToneSignature: dati di registrazione alla beta, consenso analytics, diritti GDPR, conservazione e contatti privacy.',
      canonicalPath: '/privacy',
      updated: 'Ultimo aggiornamento: 17 luglio 2026',
      intro: 'Questa Informativa sulla privacy spiega come ToneSignature raccoglie e utilizza i dati personali tramite la landing page pubblica e la registrazione alla Founding Beta.',
      sections: [
        {
          title: 'Titolare del trattamento',
          body: [
            'ToneSignature è attualmente un progetto software indipendente gestito da Omer Sorrenti.',
            'Fino alla costituzione di una persona giuridica, Omer Sorrenti agisce come titolare del trattamento dei dati personali raccolti tramite questo sito web.',
            `Per domande o richieste relative alla privacy, contatta ${privacyEmail}.`,
          ],
        },
        {
          title: 'Informazioni che raccogliamo',
          body: [
            'Quando ti iscrivi alla Founding Beta, raccogliamo il nome, l’indirizzo email, il Paese, l’hardware per chitarra selezionato, l’uso principale, la lingua preferita e la conferma di accettazione della beta inviati tramite il modulo di registrazione.',
            'Se acconsenti ai cookie analytics, possiamo raccogliere eventi analytics limitati, come interazioni con la pagina, cambi di lingua, clic sulle call to action della hero, eventi del modulo beta, interazioni con l’hardware supportato e altri segnali di interesse per il prodotto.',
          ],
        },
        {
          title: 'Perché usiamo i dati personali',
          body: [
            'Usiamo i dati di registrazione alla beta per gestire la lista della Founding Beta, comunicare con i potenziali beta user, dare priorità al supporto hardware, prevenire registrazioni duplicate e pianificare lo sviluppo del prodotto.',
            'Se viene prestato il consenso agli analytics, gli eventi raccolti ci aiutano a capire se la landing page e la demo sono chiare, affidabili e utili.',
          ],
        },
        {
          title: 'Base giuridica',
          body: [
            'Per la gestione della registrazione alla beta e le comunicazioni correlate, ci basiamo sulla tua richiesta di adottare misure precontrattuali e sul nostro legittimo interesse a gestire una beta in fase iniziale.',
            'Per i cookie analytics e gli eventi analytics, ci basiamo sul tuo consenso. Puoi rifiutare i cookie analytics senza compromettere la possibilità di usare il sito o iscriverti alla beta.',
          ],
        },
        {
          title: 'Servizi di terze parti',
          body: [
            'ToneSignature può utilizzare fornitori di servizi terzi per hosting, archiviazione database, invio email e analytics. Tali fornitori trattano dati personali solo nella misura necessaria a gestire il sito, le registrazioni alla beta e le comunicazioni correlate.',
            'I fornitori analytics vengono utilizzati solo dopo il tuo consenso e solo se è configurato un ID provider analytics. Questa informativa resta neutrale rispetto ai vendor finché i fornitori di produzione non saranno definiti.',
          ],
        },
        {
          title: 'Conservazione dei dati',
          body: [
            'I dati di registrazione alla beta sono conservati fino alla conclusione del programma beta o fino a quando richiedi la cancellazione, salvo che un periodo di conservazione più lungo sia richiesto dalla legge.',
            'I dati analytics, quando raccolti con consenso, sono conservati secondo le impostazioni del provider analytics configurato e riesaminati periodicamente in ottica di minimizzazione.',
          ],
        },
        {
          title: 'I tuoi diritti GDPR',
          body: [
            'Se il GDPR si applica al tuo caso, puoi richiedere accesso, rettifica, cancellazione, limitazione, portabilità o opposizione al trattamento dei tuoi dati personali. Puoi inoltre revocare il consenso agli analytics in qualsiasi momento.',
            `Hai anche il diritto di proporre reclamo alla tua autorità locale per la protezione dei dati. Per esercitare i tuoi diritti, scrivi a ${privacyEmail}.`,
          ],
        },
        {
          title: 'Modifiche a questa informativa',
          body: [
            'Potremmo aggiornare questa Informativa sulla privacy mentre ToneSignature evolve dalla beta verso un servizio di produzione. Le modifiche rilevanti saranno pubblicate su questa pagina.',
          ],
        },
      ],
    },
    cookies: {
      eyebrow: 'Legale',
      title: 'Informativa sui cookie',
      metaTitle: 'Informativa sui cookie | ToneSignature',
      description: 'Informativa sui cookie di ToneSignature: cookie tecnici, cookie analytics, consenso e modifica delle preferenze.',
      canonicalPath: '/cookies',
      updated: 'Ultimo aggiornamento: 17 luglio 2026',
      intro: 'Questa Informativa sui cookie spiega come ToneSignature utilizza cookie e tecnologie simili di archiviazione locale sulla landing page.',
      sections: [
        {
          title: 'Cookie tecnici e archiviazione locale',
          body: [
            'ToneSignature utilizza l’archiviazione essenziale del browser per ricordare la tua scelta sul consenso ai cookie. Questo è necessario per rispettare la tua preferenza ed evitare di mostrare ripetutamente il banner.',
            'L’archiviazione tecnica non richiede il consenso agli analytics ed è usata solo per il funzionamento di base del sito.',
          ],
        },
        {
          title: 'Cookie analytics',
          body: [
            'I cookie analytics e gli script analytics sono facoltativi. Sono usati solo per comprendere l’utilizzo aggregato del sito e migliorare la landing page, la demo e l’esperienza di registrazione alla beta.',
            'Gli analytics non vengono inizializzati finché non scegli “Accetta analytics” nel banner cookie e finché non è configurato un ID provider analytics.',
          ],
        },
        {
          title: 'Rifiuto o revoca del consenso',
          body: [
            'Puoi scegliere “Rifiuta i cookie non essenziali” nel banner per impedire il caricamento degli analytics.',
            'Puoi modificare la tua scelta in qualsiasi momento selezionando “Impostazioni cookie” nel footer. Questa azione riapre il banner cookie e ti consente di accettare analytics o rifiutare i cookie non essenziali.',
          ],
        },
        {
          title: 'Aggiornamenti futuri',
          body: [
            'Se ToneSignature aggiungerà ulteriori cookie o provider, questa informativa sarà aggiornata prima che tali cookie vengano utilizzati per finalità non essenziali.',
          ],
        },
      ],
    },
    terms: {
      eyebrow: 'Legale',
      title: 'Termini di servizio',
      metaTitle: 'Termini di servizio | ToneSignature',
      description: 'Termini di servizio della beta ToneSignature: limitazioni della beta, responsabilità sui contenuti, preset generati e limitazione di responsabilità.',
      canonicalPath: '/terms',
      updated: 'Ultimo aggiornamento: 17 luglio 2026',
      intro: 'Questi Termini di servizio regolano l’uso del sito ToneSignature, della demo interattiva e della registrazione alla Founding Beta.',
      sections: [
        {
          title: 'Servizio beta',
          body: [
            'ToneSignature è attualmente un prodotto beta in fase iniziale. Funzionalità, flussi di lavoro, supporto hardware, output generati e disponibilità possono cambiare, essere limitati o essere interrotti durante lo sviluppo del prodotto.',
            'La beta è fornita per valutazione e feedback. L’iscrizione alla beta non garantisce l’accesso, le tempistiche di rilascio o la disponibilità futura di una funzionalità specifica.',
          ],
        },
        {
          title: 'Disponibilità del servizio',
          body: [
            'ToneSignature non fornisce garanzie di uptime durante la beta. Il sito, la demo, i servizi backend e il flusso di registrazione alla beta possono non essere disponibili di tanto in tanto per manutenzione, test o sviluppo.',
          ],
        },
        {
          title: 'Contenuti degli utenti e diritti',
          body: [
            'Sei responsabile di link, audio, file o altri contenuti che fornisci a ToneSignature. Devi possedere i diritti su tali contenuti o avere tutte le autorizzazioni necessarie per usarli con il servizio.',
            'ToneSignature non rivendica alcuna proprietà sui contenuti caricati o inviati dagli utenti. Gli utenti mantengono tutti i diritti sui propri contenuti.',
            'Non caricare, inviare o trattare contenuti che violino diritti di terzi o leggi applicabili.',
          ],
        },
        {
          title: 'Preset generati',
          body: [
            'I preset generati, i suggerimenti di tono, le impostazioni dei parametri e gli output demo sono forniti come indicazioni creative e tecniche. Potrebbero richiedere regolazioni manuali in base a strumento, hardware, firmware, speaker, stile di esecuzione e ambiente di ascolto.',
            'ToneSignature non garantisce che un preset generato riproduca esattamente una registrazione, un suono di artista o un timbro commerciale.',
          ],
        },
        {
          title: 'Abbonamenti futuri',
          body: [
            'ToneSignature potrà introdurre in futuro abbonamenti, funzionalità a pagamento o piani commerciali. L’iscrizione alla Founding Beta non crea alcun obbligo di acquisto, salvo tua successiva scelta di abbonarti secondo termini di prezzo separati.',
          ],
        },
        {
          title: 'Limitazione di responsabilità',
          body: [
            'Nella misura massima consentita dalla legge, ToneSignature è fornito “così com’è” durante la beta, senza garanzie di alcun tipo. ToneSignature non sarà responsabile per danni indiretti, incidentali, consequenziali, speciali o punitivi derivanti dall’uso del sito, della demo, della beta o degli output generati.',
            'Nulla in questi Termini limita responsabilità che non possono essere limitate ai sensi della legge applicabile.',
          ],
        },
        {
          title: 'Modifiche a questi Termini',
          body: [
            'Potremmo aggiornare questi Termini con l’evoluzione della beta. I termini aggiornati saranno pubblicati su questa pagina con una data rivista.',
          ],
        },
        {
          title: 'Contatto',
          body: [
            `Per domande su questi Termini, contatta ${generalEmail}.`,
          ],
        },
      ],
    },
  },
}

type LegalPageProps = {
  page: LegalRoute
  language: Language
}

function renderParagraph(paragraph: string) {
  const email = paragraph.includes(privacyEmail)
    ? privacyEmail
    : paragraph.includes(generalEmail)
      ? generalEmail
      : null

  if (!email) {
    return paragraph
  }

  const [before, after] = paragraph.split(email)

  return <>
    {before}<a href={`mailto:${email}`}>{email}</a>{after}
  </>
}

export function LegalPage({ page, language }: LegalPageProps) {
  const content = legalPages[language][page]

  useEffect(() => {
    setPageMetadata({
      title: content.metaTitle,
      description: content.description,
      canonical: `https://tonesignature.com${content.canonicalPath}`,
    })
  }, [content])

  return <main className="legal-main">
    <section className="section legal-hero">
      <div className="container legal-shell">
        <span className="legal-eyebrow">{content.eyebrow}</span>
        <h1>{content.title}</h1>
        <p className="legal-intro">{content.intro}</p>
        <p className="legal-updated">{content.updated}</p>
      </div>
    </section>
    <section className="legal-content">
      <div className="container legal-shell">
        {content.sections.map((section) => <article key={section.title} className="legal-section">
          <h2>{section.title}</h2>
          {section.body.map((paragraph) => <p key={paragraph}>{renderParagraph(paragraph)}</p>)}
        </article>)}
      </div>
    </section>
  </main>
}
